import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { ClipLoader } from 'react-spinners';
import './SelectWorker.css';
import './SelectEquipment.css';
import { ReactComponent as ToolsIcon } from './images/tools.svg';
import { ReactComponent as VehiclesIcon } from './images/vehicles.svg';
import EquipmentCustomSelect from './EquipmentCustomSelect';

const fetchTools = async (token) => {
  const apiUrl = process.env.REACT_APP_BACKEND_API;
  const response = await fetch(`${apiUrl}/tools`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  return response.json();
};

const fetchVehicles = async (token) => {
  const apiUrl = process.env.REACT_APP_BACKEND_API;
  const response = await fetch(`${apiUrl}/vehicles`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  return response.json();
};

export default function SelectEquipment({
  equipment,
  setEquipment,
  closeShowWorkLogModal,
  setTotalExpense,
}) {
  const token = localStorage.getItem('token');
  const [isLoading, setIsLoading] = useState(true);
  const [numberOfFetchedData, setNumberOfFetchedData] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [workedTime, setWorkedTime] = useState();
  const [selectedValue, setSelectedValue] = useState('');

  const [availableTools, setAvailableTools] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);

  useQuery(['tools'], () => fetchTools(token), {
    onSuccess: (response) => {
      setNumberOfFetchedData((prevValue) => {
        if (prevValue + 1 === 2) setIsLoading(false);

        setAvailableTools(
          response.filter(
            (tool) => !equipment.some((equip) => equip.tool?.id === tool.id)
          )
        );

        return prevValue + 1;
      });
    },
    enabled: true,
  });

  useQuery(['equipment'], () => fetchVehicles(token), {
    onSuccess: (response) => {
      setNumberOfFetchedData((prevValue) => {
        if (prevValue + 1 === 2) setIsLoading(false);

        setAvailableVehicles(
          response.filter(
            (vehicle) =>
              !equipment.some((equip) => equip.vehicle?.id === vehicle.id)
          )
        );

        return prevValue + 1;
      });
    },
    enabled: true,
  });

  const handleEquipmentAddition = () => {
    const selectedEquipment =
      selectedOption === 'tools'
        ? availableTools.find((tool) => tool.id === parseInt(selectedValue))
        : availableVehicles.find(
            (vehicle) => vehicle.id === parseInt(selectedValue)
          );

    setEquipment((prevState) => {
      return [
        ...prevState,
        {
          name: selectedEquipment.name,
          currentPriceRate:
            selectedOption === 'tools'
              ? parseFloat(selectedEquipment.priceRate)
              : parseFloat(selectedEquipment.hourlyRate),
          amount: parseInt(workedTime),
          vehicle: selectedOption === 'vehicles' ? selectedEquipment : null,
          tool: selectedOption === 'tools' ? selectedEquipment : null,
        },
      ];
    });

    setTotalExpense(
      (prevTotal) =>
        prevTotal +
        parseFloat(
          selectedOption === 'vehicles'
            ? (selectedEquipment.hourlyRate / 60) * workedTime
            : selectedEquipment.rateType === 1
            ? selectedEquipment.priceRate * workedTime
            : (selectedEquipment.priceRate / 60) * workedTime
        )
    );
    closeShowWorkLogModal();
  };

  return (
    <div>
      {isLoading ? (
        <div className="select-worker-spinner">
          <ClipLoader
            color={'gray'}
            loading={isLoading}
            size={60}
            data-testid="loader"
          />
        </div>
      ) : (
        <>
          <div className="select-equipment-container">
            <h3 className="equipment-label">Odaberi opciju</h3>

            <div className="equipment-options">
              <div
                className={
                  selectedOption === 'tools'
                    ? 'equipment-option equipment-option-transition selected-option'
                    : 'equipment-option equipment-option-transition'
                }
                onClick={() => {
                  setSelectedValue('');
                  setSelectedOption('tools');
                  setWorkedTime();
                }}
              >
                <div
                  className="worker-initials"
                  style={{ backgroundColor: '#1a79cb' }}
                >
                  <ToolsIcon width={24} />
                </div>

                <div className="equipment-option-label">Alati</div>
              </div>
              <div
                className={
                  selectedOption === 'vehicles'
                    ? 'equipment-option equipment-option-transition selected-option'
                    : 'equipment-option equipment-option-transition'
                }
                onClick={() => {
                  setSelectedValue('');
                  setSelectedOption('vehicles');
                  setWorkedTime();
                }}
              >
                <div
                  className="worker-initials"
                  style={{ backgroundColor: '#1a79cb' }}
                >
                  <VehiclesIcon width={20} />
                </div>

                <div className="equipment-option-label">Vozila</div>
              </div>
            </div>

            {selectedOption && (
              <div className="equipment-selection">
                <h3 className="equipment-label">
                  Odaberi {selectedOption === 'tools' ? 'alat' : 'vozilo'}
                </h3>

                <EquipmentCustomSelect
                  selectName={'serviceType'}
                  selectList={
                    selectedOption === 'tools'
                      ? availableTools
                      : availableVehicles
                  }
                  selectedValue={selectedValue}
                  setSelectedValue={setSelectedValue}
                />
              </div>
            )}
            {selectedValue !== '' && (
              <div className="equipment-time-container">
                <div>
                  <h3 className="equipment-label">
                    {selectedOption === 'tools' &&
                    availableTools.filter(
                      (tool) => tool.id === parseInt(selectedValue)
                    )[0].rateType === 1
                      ? 'Dužina (m)'
                      : 'Vrijeme rada (min)'}
                  </h3>

                  <input
                    className="equipment-time-input"
                    type="number"
                    placeholder="0"
                    value={workedTime}
                    onChange={(e) => setWorkedTime(e.target.value)}
                  />
                </div>

                <button
                  className="add-equipment-button"
                  type="button"
                  onClick={handleEquipmentAddition}
                  disabled={workedTime <= 0 || workedTime === undefined}
                >
                  Dodaj {selectedOption === 'tools' ? 'alat' : 'vozilo'}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
